/**
 * 知识库管理页面
 *
 * 功能：文档列表（搜索+分页）、上传文件、编辑/删除、状态管理、向量化状态
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
    Button,
    Table,
    Tag,
    Input,
    Space,
    Popconfirm,
    message,
    Select,
    Tooltip,
    Modal,
} from 'antd';
import {
    PlusOutlined,
    SearchOutlined,
    EditOutlined,
    DeleteOutlined,
    CheckCircleOutlined,
    InboxOutlined,
    BellOutlined,
    CloseCircleOutlined,
    FileTextOutlined,
    SyncOutlined,
    EyeOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { knowledgeApi, taskApi, type IndexingTask } from '../../services/api';
import KnowledgeFormModal from '../../components/admin/KnowledgeFormModal';
import { TaskProgressCard } from '../../components/knowledge/TaskProgressCard';
import { useTaskWebSocket } from '../../hooks/useTaskWebSocket';
import { useAuthStore } from '../../stores/authStore';
import { formatDateTime } from '@/utils/date';

const statusMap: Record<string, { color: string; label: string; icon?: React.ReactNode }> = {
    draft: { color: 'default', label: '草稿' },
    indexing: { color: 'processing', label: '索引中', icon: <SyncOutlined spin /> },
    indexed: { color: 'cyan', label: '已索引', icon: <CheckCircleOutlined /> },
    published: { color: 'green', label: '已发布', icon: <CheckCircleOutlined /> },
    archived: { color: 'orange', label: '已归档', icon: <InboxOutlined /> },
    failed: { color: 'red', label: '索引失败', icon: <CloseCircleOutlined /> },
};

const KnowledgePage: React.FC = () => {
    const { user } = useAuthStore();
    const navigate = useNavigate();
    const [documents, setDocuments] = useState<any[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [pageSize] = useState(10);
    const [keyword, setKeyword] = useState('');
    const [statusFilter, setStatusFilter] = useState<string | undefined>();
    const [modalOpen, setModalOpen] = useState(false);
    const [editData, setEditData] = useState<any>(null);
    const [searchMode, setSearchMode] = useState<'keyword' | 'semantic'>('keyword');

    // 任务进度详情弹窗
    const [taskModalOpen, setTaskModalOpen] = useState(false);
    const [currentTask, setCurrentTask] = useState<IndexingTask | null>(null);
    const [taskDocTitle, setTaskDocTitle] = useState<string>('');
    const currentTaskId = currentTask?.id;
    const currentTaskStatus = currentTask?.status;

    const fetchDocuments = useCallback(async () => {
        setLoading(true);
        try {
            if (searchMode === 'semantic' && keyword.trim()) {
                // 语义搜索模式
                const res = await knowledgeApi.search(keyword, 20);
                setDocuments(res.data.results || []);
                setTotal(res.data.total || 0);
            } else {
                // 普通列表模式
                const res = await knowledgeApi.list({
                    page,
                    page_size: pageSize,
                    keyword: keyword || undefined,
                    status_filter: statusFilter,
                });
                setDocuments(res.data.items || []);
                setTotal(res.data.total || 0);
            }
        } catch (err) {
            message.error('获取文档列表失败');
        } finally {
            setLoading(false);
        }
    }, [page, pageSize, keyword, statusFilter, searchMode]);

    // 弹窗打开时，轮询刷新任务进度（解决 WebSocket 无主动推送的问题）
    useEffect(() => {
        if (!taskModalOpen || !currentTaskId || !currentTaskStatus) return;

        // 已终结状态不再轮询
        const terminalStatuses = ['completed', 'failed', 'cancelled'];
        if (terminalStatuses.includes(currentTaskStatus)) return;

        const timer = setInterval(async () => {
            try {
                const updated = await taskApi.get(currentTaskId);
                setCurrentTask(updated);

                // 如果任务已完成/失败，停止轮询并刷新文档列表
                if (terminalStatuses.includes(updated.status)) {
                    clearInterval(timer);
                    setTimeout(() => fetchDocuments(), 1000);
                }
            } catch (err) {
                console.warn('[TaskPolling] 轮询任务进度失败:', err);
            }
        }, 2000);

        return () => clearInterval(timer);
    }, [taskModalOpen, currentTaskId, currentTaskStatus, fetchDocuments]);

    // WebSocket 事件处理

    const handleProgress = useCallback((event: any) => {
        setCurrentTask(prev => {
            if (prev && event.task_id === prev.id) {
                return {
                    ...prev,
                    progress: event.progress,
                    current_stage: event.current_stage || prev.current_stage,
                    processed_chunks: event.processed_chunks || prev.processed_chunks,
                    total_chunks: event.total_chunks || prev.total_chunks,
                    estimated_remaining: event.estimated_remaining || prev.estimated_remaining,
                };
            }
            return prev;
        });

        // 同时更新 documents 中的索引中项
        setDocuments(prev => prev.map(doc => {
            // doc.task_id 是旧逻辑里关联的，如果仍存在就更新列表的进度
            if (doc.task_id === event.task_id) {
                return {
                    ...doc,
                    progress: event.progress,
                    current_stage: event.current_stage,
                };
            }
            return doc;
        }));
    }, []);

    const handleCompleted = useCallback((event: any) => {
        setCurrentTask(prev => {
            if (prev && event.task_id === prev.id) {
                return {
                    ...prev,
                    status: 'completed',
                    progress: 100,
                };
            }
            return prev;
        });
        // 3 秒后刷新列表
        setTimeout(() => fetchDocuments(), 3000);
    }, [fetchDocuments]);

    const handleFailed = useCallback((event: any) => {
        setCurrentTask(prev => {
            if (prev && event.task_id === prev.id) {
                return {
                    ...prev,
                    status: 'failed',
                    error_message: event.error_message,
                };
            }
            return prev;
        });
    }, []);

    // 连接 WebSocket（订阅所有索引中的任务）
    const indexingTaskIds = documents
        .filter(d => d.status === 'indexing' && d.task_id)
        .map(d => d.task_id);

    const { connected, subscribe } = useTaskWebSocket({
        taskIds: indexingTaskIds,
        onProgress: handleProgress,
        onCompleted: handleCompleted,
        onFailed: handleFailed,
        autoConnect: true,
    });

    useEffect(() => {
        fetchDocuments();
    }, [fetchDocuments]);
    // 定时刷新：处理 indexing 状态自动更新
    useEffect(() => {
        const hasIndexing = documents.some((d) => d.status === 'indexing');
        if (!hasIndexing) return;

        const timer = setInterval(() => {
            fetchDocuments();
        }, 3000);

        return () => clearInterval(timer);
    }, [documents, fetchDocuments]);

    const handleDelete = async (id: number) => {
        try {
            await knowledgeApi.delete(id);
            message.success('删除成功');
            fetchDocuments();
        } catch {
            message.error('删除失败');
        }
    };

    const handlePublish = async (id: number, currentStatus: string) => {
        if (currentStatus !== 'indexed') {
            message.warning('仅已完成索引的文档可以发布');
            return;
        }
        try {
            await knowledgeApi.publish(id);
            message.success('发布成功');
            fetchDocuments();
        } catch (err: any) {
            message.error(err.response?.data?.detail || '发布失败');
        }
    };

    const handleArchive = async (id: number) => {
        try {
            await knowledgeApi.archive(id);
            message.success('归档成功');
            fetchDocuments();
        } catch {
            message.error('归档失败');
        }
    };

    const handleEdit = async (record: any) => {
        try {
            const res = await knowledgeApi.get(record.id);
            setEditData(res.data);
            setModalOpen(true);
        } catch {
            message.error('获取文档详情失败');
        }
    };

    // 查看任务进度
    const handleViewTaskProgress = async (record: any) => {
        try {
            // 首先查询该文档下的所有任务
            const res = await taskApi.listByDoc(record.id);
            if (!res.tasks || res.tasks.length === 0) {
                message.warning('该文档暂无关联的索引任务');
                return;
            }

            // 获取最新（排在第一）的任务 ID
            const latestTaskId = res.tasks[0].id;
            if (!latestTaskId) {
                message.warning('任务数据异常');
                return;
            }

            // 获取该任务的详情与实时进度
            const task = await taskApi.get(latestTaskId);
            setCurrentTask(task);
            setTaskDocTitle(record.title);
            setTaskModalOpen(true);

            // 如果任务还在处理中，订阅其实时进度更新
            if (task.status === 'processing' || task.status === 'pending') {
                subscribe([latestTaskId]);
            }
        } catch (err: any) {
            console.error('Failed to get task details:', err);
            message.error('获取任务详情失败');
        }
    };

    // 取消任务
    const handleCancelTask = async (taskId: number) => {
        try {
            await taskApi.cancel(taskId);
            setCurrentTask(prev => prev ? { ...prev, status: 'cancelled' } : null);
            message.success('任务已取消');
            setTimeout(() => fetchDocuments(), 2000);
        } catch {
            message.error('取消任务失败');
        }
    };

    // 重试任务
    const handleRetryTask = async (taskId: number) => {
        try {
            await taskApi.retry(taskId);
            setCurrentTask(prev => prev ? { ...prev, status: 'pending', progress: 0 } : null);
            message.success('任务已重新排队');
        } catch (err: any) {
            message.error(err.message || '重试任务失败');
        }
    };

    const columns = [
        {
            title: '标题',
            dataIndex: 'title',
            key: 'title',
            ellipsis: true,
            width: 250,
            render: (val: string, record: any) => (
                <a
                    onClick={() => navigate(`/admin/knowledge/${record.id}`)}
                    style={{ color: '#3b82f6', cursor: 'pointer', fontWeight: 500 }}
                >
                    <FileTextOutlined style={{ marginRight: 6 }} />
                    {val}
                </a>
            ),
        },
        {
            title: '分类',
            dataIndex: 'category',
            key: 'category',
            width: 100,
            render: (val: string) =>
                val ? <Tag>{val}</Tag> : <span style={{ color: '#a1a1aa' }}>—</span>,
        },
        {
            title: '状态',
            dataIndex: 'status',
            key: 'status',
            width: 180,
            render: (val: string, record: any) => {
                const info = statusMap[val] || { color: 'default', label: val };
                return (
                    <Space direction="vertical" size={2}>
                        <Tag color={info.color} icon={info.icon}>
                            {info.label}
                        </Tag>
                        {val === 'indexing' && record.progress !== undefined && (
                            <div style={{ fontSize: 12, color: '#999' }}>
                                进度：{record.progress}%
                            </div>
                        )}
                    </Space>
                );
            },
        },
        {
            title: '标签',
            dataIndex: 'tags',
            key: 'tags',
            width: 160,
            render: (tags: string[]) =>
                tags?.length > 0
                    ? tags.map((t) => (
                        <Tag key={t} color="blue" style={{ marginBottom: 2 }}>
                            {t}
                        </Tag>
                    ))
                    : <span style={{ color: '#a1a1aa' }}>—</span>,
        },
        {
            title: '文件',
            dataIndex: 'file_info',
            key: 'file_info',
            width: 100,
            render: (info: any) =>
                info ? (
                    <Tooltip title={info.name}>
                        <span style={{ color: '#71717a', fontSize: 12 }}>
                            {info.name?.length > 12
                                ? info.name.slice(0, 12) + '...'
                                : info.name}
                        </span>
                    </Tooltip>
                ) : (
                    <span style={{ color: '#a1a1aa' }}>—</span>
                ),
        },
        {
            title: '更新时间',
            dataIndex: 'updated_at',
            key: 'updated_at',
            width: 170,
            render: (val: string) =>
                formatDateTime(val),
        },
        ...(searchMode === 'semantic' && keyword.trim()
            ? [
                {
                    title: '相关度',
                    dataIndex: 'score',
                    key: 'score',
                    width: 80,
                    render: (val: number) =>
                        val ? (
                            <Tag color="purple">{(val * 100).toFixed(0)}%</Tag>
                        ) : (
                            '—'
                        ),
                },
            ]
            : []),
        {
            title: '操作',
            key: 'actions',
            width: 180,
            render: (_: any, record: any) => (
                <Space size={4}>
                    {['indexing', 'indexed', 'published', 'failed'].includes(record.status) && (
                        <Tooltip title="查看进度/任务">
                            <Button
                                type="link"
                                size="small"
                                icon={<EyeOutlined />}
                                onClick={() => handleViewTaskProgress(record)}
                            />
                        </Tooltip>
                    )}
                    <Tooltip title="编辑">
                        <Button
                            type="link"
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => handleEdit(record)}
                        />
                    </Tooltip>
                    {(record.status === 'indexed') && (
                        <Tooltip title="发布">
                            <Button
                                type="link"
                                size="small"
                                icon={<CheckCircleOutlined />}
                                style={{ color: '#16a34a' }}
                                onClick={() => handlePublish(record.id, record.status)}
                            />
                        </Tooltip>
                    )}
                    {record.status === 'published' && (
                        <Tooltip title="归档">
                            <Button
                                type="link"
                                size="small"
                                icon={<InboxOutlined />}
                                style={{ color: '#f59e0b' }}
                                onClick={() => handleArchive(record.id)}
                            />
                        </Tooltip>
                    )}
                    <Popconfirm
                        title="确定删除此文档？"
                        onConfirm={() => handleDelete(record.id)}
                        okText="删除"
                        cancelText="取消"
                    >
                        <Tooltip title="删除">
                            <Button
                                type="link"
                                size="small"
                                danger
                                icon={<DeleteOutlined />}
                            />
                        </Tooltip>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* 顶部栏 */}
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '0 32px',
                    height: 64,
                    background: '#fff',
                    borderBottom: '1px solid #e4e4e7',
                    flexShrink: 0,
                }}
            >
                <h2 style={{ margin: 0, fontSize: 20, fontWeight: 600, color: '#18181b' }}>
                    知识库配置
                </h2>
                <Space>
                    <BellOutlined style={{ fontSize: 18, color: '#71717a', cursor: 'pointer' }} />
                    <div
                        style={{
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            background: '#3b82f6',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#fff',
                            fontWeight: 600,
                            fontSize: 14,
                        }}
                    >
                        {user?.username?.[0]?.toUpperCase() || 'A'}
                    </div>
                </Space>
            </div>

            {/* 内容区 */}
            <div style={{ flex: 1, padding: 24, overflow: 'auto' }}>
                {/* 操作栏 */}
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: 16,
                    }}
                >
                    <Space>
                        <Select
                            value={searchMode}
                            onChange={(val) => { setSearchMode(val); setPage(1); }}
                            style={{ width: 110 }}
                            options={[
                                { label: '关键词', value: 'keyword' },
                                { label: '语义搜索', value: 'semantic' },
                            ]}
                        />
                        <Input
                            placeholder={searchMode === 'semantic' ? '输入自然语言搜索...' : '搜索文档...'}
                            prefix={<SearchOutlined style={{ color: '#a1a1aa' }} />}
                            value={keyword}
                            onChange={(e) => setKeyword(e.target.value)}
                            onPressEnter={() => { setPage(1); fetchDocuments(); }}
                            style={{ width: 280 }}
                            allowClear
                        />
                        <Select
                            placeholder="状态筛选"
                            value={statusFilter}
                            onChange={(val) => { setStatusFilter(val); setPage(1); }}
                            allowClear
                            style={{ width: 120 }}
                            options={[
                                { label: '草稿', value: 'draft' },
                                { label: '索引中', value: 'indexing' },
                                { label: '已索引', value: 'indexed' },
                                { label: '已发布', value: 'published' },
                                { label: '已归档', value: 'archived' },
                                { label: '索引失败', value: 'failed' },
                            ]}
                        />
                    </Space>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => {
                            setEditData(null);
                            setModalOpen(true);
                        }}
                    >
                        上传文档
                    </Button>
                </div>

                {/* 文档表格 */}
                <Table
                    columns={columns}
                    dataSource={documents}
                    rowKey="id"
                    loading={loading}
                    pagination={
                        searchMode === 'semantic' && keyword.trim()
                            ? false
                            : {
                                current: page,
                                pageSize,
                                total,
                                showTotal: (t) => `共 ${t} 条`,
                                onChange: (p) => setPage(p),
                            }
                    }
                    style={{
                        background: '#fff',
                        borderRadius: 12,
                    }}
                />
            </div>

            {/* 表单模态框 */}
            <KnowledgeFormModal
                open={modalOpen}
                editData={editData}
                onClose={() => {
                    setModalOpen(false);
                    setEditData(null);
                }}
                onSuccess={fetchDocuments}
            />

            {/* 任务进度详情弹窗 */}
            <Modal
                open={taskModalOpen}
                onCancel={() => setTaskModalOpen(false)}
                footer={null}
                width={720}
                title={
                    <Space>
                        <FileTextOutlined />
                        <span>「{taskDocTitle}」索引进度</span>
                        {connected && (
                            <Tag color="green" style={{ marginLeft: 8 }}>
                                实时更新中
                            </Tag>
                        )}
                    </Space>
                }
            >
                {currentTask && (
                    <TaskProgressCard
                        task={currentTask}
                        compact={false}
                        showActions
                        onCancel={handleCancelTask}
                        onRetry={handleRetryTask}
                    />
                )}
            </Modal>
        </div>
    );
};

export default KnowledgePage;
